/**
 * Crop a loaded image to a square blob for avatar upload.
 */

export const AVATAR_OUTPUT_SIZE = 512;

/**
 * Decode an image URL (object URL or data URL) into an HTMLImageElement.
 * @param {string} src
 * @returns {Promise<HTMLImageElement>}
 */
export function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () =>
      reject(new Error("Could not load image"))
    );
    image.src = src;
  });
}

/**
 * Draw the selected crop region onto a canvas and return a PNG blob.
 * Downscales to {@link AVATAR_OUTPUT_SIZE} but never upscales.
 *
 * @param {string} imageSrc
 * @param {{ x: number, y: number, width: number, height: number }} pixelCrop
 * @returns {Promise<Blob>}
 */
export async function getCroppedAvatarBlob(imageSrc, pixelCrop) {
  const image = await loadHtmlImage(imageSrc);
  const sourceWidth = Math.max(1, Math.round(pixelCrop.width));
  const sourceHeight = Math.max(1, Math.round(pixelCrop.height));
  const sourceX = Math.max(0, Math.round(pixelCrop.x));
  const sourceY = Math.max(0, Math.round(pixelCrop.y));
  const outputSize = Math.min(AVATAR_OUTPUT_SIZE, sourceWidth, sourceHeight);

  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not crop image");
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    outputSize,
    outputSize
  );

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) =>
        result ? resolve(result) : reject(new Error("Could not crop image")),
      "image/png"
    );
  });
  return blob;
}
